import { useMemo, useState } from 'preact/hooks';
import {
  type CheckoutFormFieldId,
  type CheckoutSessionDto,
  type FieldValidator,
  type FormFieldDefinitionDto,
  createSession,
} from './api.js';
import { DynamicForm, type DynamicFormPrefill } from './DynamicForm.js';
import { buildFieldValidator, type FieldValidatorRuntime } from './field-validator.js';

/**
 * Reference-lookup form. Two render paths:
 *
 *   1. **Dynamic (operator-authored).** When `formFields` is non-empty the
 *      form is composed entirely from the operator's declarative field list —
 *      no field id is hardcoded in this component. Submission keys flow from
 *      each field's `inputMap || id`.
 *
 *   2. **Legacy hardcoded pair (back-compat).** When `formFields` is empty /
 *      missing we render today's `referenceNumber` + `email` pair so tenants
 *      that haven't migrated keep working. The legacy branch submits the
 *      reference under the canonical `amentumReferenceNumber` upstream key,
 *      matching what the SPA shipped before this refactor.
 *
 * The Lookup contract (props) is unchanged so `App.tsx` keeps compiling
 * without modification.
 */
type Validators = Partial<Record<CheckoutFormFieldId, FieldValidator>>;

export function Lookup(props: {
  tenant: string;
  strategy: string;
  submitLabel: string;
  referenceHelpText?: string;
  methodSelected: boolean;
  prefill?: { reference?: string; email?: string; name?: string };
  /** Per-field validation rules authored in the admin builder. */
  validators?: Validators;
  /**
   * Operator-authored form fields. When present + non-empty the form is built
   * from this list and the legacy `validators` prop is ignored (each field
   * carries its own validator). Empty / missing => legacy render path.
   */
  formFields?: FormFieldDefinitionDto[];
  /** Locale tag used to render labels / placeholders / help text. Defaults to `en`. */
  locale?: string;
  onValidated: (s: CheckoutSessionDto) => void;
  onError: (msg: string) => void;
}) {
  // Operator-authored dynamic mode wins when fields exist.
  if (props.formFields && props.formFields.length > 0) {
    // Translate the legacy `prefill` shape into the dynamic form's id-keyed
    // map. We map `reference -> referenceNumber` so existing query-param
    // mappings keep pre-filling the same first text input.
    const dynamicPrefill: DynamicFormPrefill = {};
    if (props.prefill?.reference !== undefined) {
      dynamicPrefill.referenceNumber = props.prefill.reference;
      // Also seed under the generic `reference` id in case the operator named
      // their field that. The DynamicForm only consumes keys it has fields for.
      dynamicPrefill.reference = props.prefill.reference;
    }
    if (props.prefill?.email !== undefined) dynamicPrefill.email = props.prefill.email;
    if (props.prefill?.name !== undefined) dynamicPrefill.name = props.prefill.name;

    return (
      <DynamicForm
        tenant={props.tenant}
        strategy={props.strategy}
        fields={props.formFields}
        submitLabel={props.submitLabel}
        methodSelected={props.methodSelected}
        locale={props.locale ?? 'en'}
        prefill={dynamicPrefill}
        onValidated={props.onValidated}
        onError={props.onError}
        // exactOptionalPropertyTypes: pass `footerHelpText` only when set.
        {...(props.referenceHelpText !== undefined
          ? { footerHelpText: props.referenceHelpText }
          : {})}
      />
    );
  }

  return <LegacyLookup {...props} />;
}

/**
 * The pre-refactor hardcoded reference + email form. Preserved verbatim so
 * tenants that haven't published a `formFields` definition behave identically.
 * Deleting this branch is a future cleanup once every tenant has migrated.
 */
function LegacyLookup(props: {
  tenant: string;
  strategy: string;
  submitLabel: string;
  referenceHelpText?: string;
  methodSelected: boolean;
  prefill?: { reference?: string; email?: string; name?: string };
  validators?: Validators;
  onValidated: (s: CheckoutSessionDto) => void;
  onError: (msg: string) => void;
}) {
  const [ref, setRef] = useState(props.prefill?.reference ?? '');
  const [email, setEmail] = useState(props.prefill?.email ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ referenceNumber?: string; email?: string }>({});

  const refRuntime = useFieldValidator(props.validators?.referenceNumber);
  const emailRuntime = useFieldValidator(props.validators?.email);

  function validateField(field: CheckoutFormFieldId, raw: string): boolean {
    const rt = field === 'referenceNumber' ? refRuntime : field === 'email' ? emailRuntime : null;
    if (!rt) return true;
    const r = rt.run(raw);
    setErrors((e) => ({ ...e, [field]: r.ok ? undefined : r.error }));
    return r.ok;
  }

  async function submit(e: Event): Promise<void> {
    e.preventDefault();
    if (submitting) return;
    const refResult = refRuntime.run(ref);
    const emailResult = emailRuntime.run(email);
    if (!refResult.ok || !emailResult.ok) {
      setErrors({
        ...(refResult.ok ? {} : { referenceNumber: refResult.error }),
        ...(emailResult.ok ? {} : { email: emailResult.error }),
      });
      return;
    }
    setSubmitting(true);
    try {
      const s = await createSession({
        tenant: props.tenant,
        strategy_name: props.strategy,
        validator_inputs: { amentumReferenceNumber: refResult.value },
        shopper: emailResult.value ? { email: emailResult.value } : {},
      });
      props.onValidated(s);
    } catch (e) {
      props.onError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  const hasErrors = Boolean(errors.referenceNumber) || Boolean(errors.email);

  return (
    <form
      onSubmit={submit}
      class="bg-surface-container-lowest border border-outline-variant rounded-xl p-4 sm:p-6 lg:p-8 shadow-sm space-y-3 lg:space-y-4"
    >
      <Field
        label="Reference number"
        placeholder="e.g. 100000001"
        value={ref}
        font="font-mono"
        error={errors.referenceNumber}
        onInput={(v) => {
          setRef(v);
          if (v.length === 0) {
            setErrors((e) => ({ ...e, referenceNumber: undefined }));
          } else {
            validateField('referenceNumber', v);
          }
        }}
        required={props.validators?.referenceNumber?.required ?? true}
      />
      <Field
        label="Your email (optional)"
        placeholder="you@example.com"
        value={email}
        type="email"
        error={errors.email}
        onInput={(v) => {
          setEmail(v);
          if (v.length === 0) {
            setErrors((e) => ({ ...e, email: undefined }));
          } else {
            validateField('email', v);
          }
        }}
        required={props.validators?.email?.required ?? false}
      />
      <button
        type="submit"
        disabled={submitting || !props.methodSelected || hasErrors}
        title={
          !props.methodSelected
            ? 'Pick a payment method to continue'
            : hasErrors
              ? 'Fix the errors above'
              : undefined
        }
        class="w-full mt-2 px-4 py-2.5 bg-primary text-on-primary text-xs uppercase tracking-widest font-semibold rounded hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? 'Verifying…' : props.submitLabel}
      </button>
      {!props.methodSelected && (
        <p class="text-[11px] text-on-surface-variant pt-1">
          Pick a payment method above to continue.
        </p>
      )}
      {props.referenceHelpText && (
        <p class="text-body-sm text-on-surface-variant pt-1">{props.referenceHelpText}</p>
      )}
    </form>
  );
}

function Field(props: {
  label: string;
  placeholder: string;
  value: string;
  type?: string;
  font?: string;
  required?: boolean;
  error?: string;
  onInput: (v: string) => void;
}) {
  const hasError = Boolean(props.error);
  return (
    <label class="block">
      <span class="upg-eyebrow text-on-primary-container">{props.label}</span>
      <input
        class={`mt-1 w-full px-3 py-2 border rounded text-body-md outline-none ${props.font ?? ''} ${
          hasError
            ? 'border-error focus:border-error'
            : 'border-outline-variant focus:border-on-surface'
        }`}
        value={props.value}
        onInput={(e) => props.onInput((e.currentTarget as HTMLInputElement).value)}
        placeholder={props.placeholder}
        type={props.type ?? 'text'}
        aria-invalid={hasError ? 'true' : 'false'}
        required={props.required}
      />
      {hasError && (
        <p class="text-[11px] text-error mt-1" role="alert">
          {props.error}
        </p>
      )}
    </label>
  );
}

/**
 * Memoise the runtime so we don't recompile the regex on every render.
 * `useMemo` keys on the JSON serialisation of the rule so a deep-equal
 * rule produces the same runtime — the admin builder can re-emit the
 * object without forcing a refresh of every validator.
 */
function useFieldValidator(rule: FieldValidator | undefined): FieldValidatorRuntime {
  const key = JSON.stringify(rule ?? {});
  return useMemo(() => buildFieldValidator(rule ?? {}), [key]); // eslint-disable-line react-hooks/exhaustive-deps
}
